/**
 * PediatricsPanel — additive top card for specialty.mode === 'soap+peds'
 * ────────────────────────────────────────────────────────────────────
 * Real percentile calculations against embedded WHO LMS tables
 * (`../../../config/growthCurves`) and real ACIP schedule status
 * (`../../../config/acipSchedule`). Clinician can adjust age,
 * sex, and current doses; percentiles + due-list update live.
 */
import { useMemo, useState } from 'react';
import { Baby, Syringe, Ruler, Activity, ClipboardCheck, Scale, Info } from 'lucide-react';
import { toast } from 'sonner';
import { percentile } from '../../../../config/growthCurves';
import { acipAssessment, ANTIGENS } from '../../../../config/acipSchedule';

/* Seed measurements that look believable across age bands. */
const SEED_VITALS = {
  12: { weight: 9.8,  length: 76.0, hc: 45.8 },
  18: { weight: 11.2, length: 82.5, hc: 47.3 },
  24: { weight: 12.4, length: 87.2, hc: 48.2 },
  30: { weight: 13.6, length: 92.1, hc: 48.9 },
  36: { weight: 14.8, length: 96.0, hc: 49.5 },
};

/* Seed dose counts mapped to age band. `received[antigen] = doses`. */
const SEED_DOSES = {
  12: { hepb: 3, dtap: 3, hib: 3, pcv13: 3, ipv: 2, rota: 3 },
  18: { hepb: 3, dtap: 3, hib: 3, pcv13: 3, ipv: 2, rota: 3, hepa: 0, mmr: 0, var: 0 }, // Will flag mmr/var/hepa as due
  24: { hepb: 3, dtap: 4, hib: 4, pcv13: 4, ipv: 3, rota: 3, mmr: 1, var: 1, hepa: 1 },
  30: { hepb: 3, dtap: 4, hib: 4, pcv13: 4, ipv: 3, rota: 3, mmr: 1, var: 1, hepa: 2 },
  36: { hepb: 3, dtap: 4, hib: 4, pcv13: 4, ipv: 3, rota: 3, mmr: 1, var: 1, hepa: 2 },
};

const PctBar = ({ pct }) => {
  const tone = pct >= 97 || pct <= 3 ? 'red' : pct >= 85 || pct <= 15 ? 'amber' : 'green';
  const barColor = tone === 'red' ? 'bg-red-500' : tone === 'amber' ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="h-1 rounded-full bg-slate-100 relative overflow-hidden">
      <div className={`h-full ${barColor}`} style={{ width: `${Math.max(2, Math.min(98, pct))}%` }} />
    </div>
  );
};

const GrowthRow = ({ icon: Icon, label, value, unit, pct, z }) => (
  <div className="space-y-1" title={`Z-score ${z}`}>
    <Icon className="h-3 w-3 text-sky-600 mb-0.5" />
    <div className="flex items-baseline justify-between">
      <p className="text-[10px] text-slate-500">{label}</p>
      <p className="text-[11px] font-semibold text-slate-800 tabular-nums">
        {value}<span className="text-[9px] text-slate-400">{unit}</span>
        <span className="ml-1 text-[10px] text-slate-500">· {pct}<sup>th</sup>%</span>
      </p>
    </div>
    <PctBar pct={pct} />
  </div>
);

const StatusChip = ({ status }) => {
  const style = {
    complete:   'bg-emerald-100 text-emerald-800',
    up_to_date: 'bg-slate-100 text-slate-600',
    due_now:    'bg-amber-100 text-amber-800',
    catch_up:   'bg-red-100 text-red-800',
  }[status] || 'bg-slate-100 text-slate-600';
  const label = {
    complete: 'complete', up_to_date: 'up to date',
    due_now: 'due now', catch_up: 'catch-up',
  }[status];
  return <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${style}`}>{label}</span>;
};

export const PediatricsPanel = () => {
  const [ageMonths, setAgeMonths] = useState(18);
  const [sex, setSex] = useState('male');

  const nearestSeedAge = useMemo(() => {
    const keys = Object.keys(SEED_VITALS).map(Number);
    return keys.reduce((a, b) => Math.abs(b - ageMonths) < Math.abs(a - ageMonths) ? b : a, keys[0]);
  }, [ageMonths]);
  const [vitals, setVitals] = useState(SEED_VITALS[nearestSeedAge]);
  const [doses, setDoses]   = useState(SEED_DOSES[nearestSeedAge]);

  /* When age band changes via slider-step, reset to the matching seed. */
  const onAgeChange = (next) => {
    setAgeMonths(next);
    const keys = Object.keys(SEED_VITALS).map(Number);
    const newSeed = keys.reduce((a, b) => Math.abs(b - next) < Math.abs(a - next) ? b : a, keys[0]);
    setVitals(SEED_VITALS[newSeed]);
    setDoses(SEED_DOSES[newSeed]);
  };

  /* Live growth percentiles from WHO LMS. */
  const heightM = vitals.length / 100;
  const bmi     = vitals.weight / (heightM * heightM);
  const wPct  = percentile('weight', sex, ageMonths, vitals.weight);
  const lPct  = percentile('length', sex, ageMonths, vitals.length);
  const hcPct = percentile('hc',     sex, ageMonths, vitals.hc);
  const bPct  = percentile('bmi',    sex, ageMonths, bmi);

  /* Live ACIP status. */
  const acip = useMemo(() => acipAssessment(ageMonths, doses), [ageMonths, doses]);
  const dueNow = acip.filter((a) => a.status === 'due_now' || a.status === 'catch_up');

  const administer = (id) =>
    setDoses((prev) => {
      const max = ANTIGENS[id]?.max || 10;
      return { ...prev, [id]: Math.min(max, (prev[id] || 0) + 1) };
    });

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/30 p-4 space-y-3" data-testid="peds-panel">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <Baby className="h-4 w-4 text-sky-700" />
        <p className="text-xs font-bold text-slate-900">Peds growth &amp; development</p>
        <span className="text-[9px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold">
          {Math.floor(ageMonths / 12) > 0 ? `${Math.floor(ageMonths / 12)}y ` : ''}{ageMonths % 12}m · {sex === 'male' ? 'M' : 'F'}
        </span>
        {dueNow.length > 0 && (
          <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
            {dueNow.length} vaccine{dueNow.length !== 1 ? 's' : ''} due
          </span>
        )}
        <button
          type="button"
          onClick={() => toast.success('Added peds summary to note')}
          className="ml-auto text-[10px] font-semibold text-slate-600 hover:text-slate-900 px-2 py-1 rounded bg-white border border-slate-200"
          data-testid="peds-add-to-note"
        >
          + Add to note
        </button>
      </div>

      {/* Controls: age slider + sex toggle */}
      <div className="flex items-center gap-3 text-[11px] p-2 rounded-lg bg-white border border-slate-100" data-testid="peds-controls">
        <Scale className="h-3 w-3 text-slate-500" />
        <span className="font-semibold text-slate-700">Age</span>
        <input
          type="range" min={2} max={60} step={1} value={ageMonths}
          onChange={(e) => onAgeChange(+e.target.value)}
          className="flex-1 accent-sky-600"
          data-testid="peds-age-slider"
        />
        <span className="tabular-nums font-semibold text-slate-800 w-14 text-right">{ageMonths} mo</span>
        <div className="inline-flex rounded-md bg-slate-100 p-0.5">
          {['male', 'female'].map((s) => (
            <button
              key={s}
              onClick={() => setSex(s)}
              className={`px-2 py-0.5 text-[10px] font-semibold transition rounded ${
                sex === s ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`peds-sex-${s}`}
            >
              {s === 'male' ? 'Boy' : 'Girl'}
            </button>
          ))}
        </div>
      </div>

      {/* Growth row — real WHO LMS percentiles */}
      <div className="grid grid-cols-4 gap-3 p-3 rounded-lg bg-white border border-slate-100" data-testid="peds-growth">
        <GrowthRow icon={Activity} label="Weight-for-age" value={vitals.weight} unit=" kg"  pct={wPct.percentile}  z={wPct.z} />
        <GrowthRow icon={Ruler}    label="Length-for-age" value={vitals.length} unit=" cm"  pct={lPct.percentile}  z={lPct.z} />
        <GrowthRow icon={Ruler}    label="Head circumference" value={vitals.hc} unit=" cm"  pct={hcPct.percentile} z={hcPct.z} />
        <GrowthRow icon={Activity} label="BMI-for-age"    value={bmi.toFixed(1)} unit=""    pct={bPct.percentile}  z={bPct.z} />
      </div>
      <p className="text-[9px] text-slate-400 flex items-center gap-1">
        <Info className="h-2.5 w-2.5" /> Percentiles computed live from WHO Child Growth Standards LMS tables (0–36 months).
      </p>

      {/* ACIP immunizations — real schedule */}
      <div className="p-3 rounded-lg bg-white border border-slate-100" data-testid="peds-vaccines">
        <div className="flex items-center gap-2 mb-2">
          <Syringe className="h-3.5 w-3.5 text-slate-500" />
          <p className="text-[11px] font-bold text-slate-800">ACIP immunizations · age {ageMonths} mo</p>
          <span className="text-[9px] text-slate-400 ml-auto">
            {acip.filter((a) => a.status === 'complete').length} complete ·
            {' '}{acip.filter((a) => a.status === 'up_to_date').length} on schedule ·
            {' '}<span className="text-amber-700 font-semibold">{dueNow.length} need action</span>
          </span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {acip.map((v) => {
            const tone = v.status === 'complete' ? 'emerald'
                       : v.status === 'catch_up' ? 'red'
                       : v.status === 'due_now'  ? 'amber' : 'slate';
            const bg = tone === 'emerald' ? 'border-emerald-100 bg-emerald-50/40'
                     : tone === 'red'     ? 'border-red-200 bg-red-50/50'
                     : tone === 'amber'   ? 'border-amber-200 bg-amber-50/50'
                     : 'border-slate-200 bg-slate-50';
            return (
              <div
                key={v.id}
                className={`flex items-center justify-between px-2 py-1 rounded border ${bg}`}
                data-testid={`peds-vax-${v.id}`}
                title={v.message}
              >
                <div className="min-w-0">
                  <p className="text-[11px] text-slate-800 truncate">{v.label}</p>
                  <p className="text-[9px] text-slate-500 truncate">{v.received}/{v.max} doses · {v.message}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <StatusChip status={v.status} />
                  {v.status !== 'complete' && (
                    <button
                      type="button"
                      onClick={() => administer(v.id)}
                      className="text-[9px] font-semibold text-slate-700 hover:text-slate-900 bg-white px-1.5 py-0.5 rounded border border-slate-200"
                      data-testid={`peds-vax-give-${v.id}`}
                    >
                      Give
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PediatricsPanel;
